import { useEffect } from 'react';
import { useEditorContext } from '../context/EditorContext';

/**
 * Global keyboard shortcuts for the editor.
 * Must be mounted inside <EditorProvider>.
 *
 * Ctrl / Cmd + Z        → Undo
 * Ctrl / Cmd + Shift+Z  → Redo
 * Ctrl / Cmd + Y        → Redo (Windows alt)
 * Escape                → Deselect current layer
 */
export function useKeyboardShortcuts() {
  const { undo, redo, selectLayer, canUndo, canRedo } = useEditorContext();

  useEffect(() => {
    const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform);

    const handler = (e: KeyboardEvent) => {
      // Ignore events inside text inputs / textareas / selects
      const tag = (e.target as HTMLElement)?.tagName ?? '';
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return;

      const cmd = isMac ? e.metaKey : e.ctrlKey;

      if (cmd && !e.shiftKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (canUndo) undo();
        return;
      }

      if (cmd && (e.shiftKey && e.key.toLowerCase() === 'z') || (cmd && e.key.toLowerCase() === 'y')) {
        e.preventDefault();
        if (canRedo) redo();
        return;
      }

      if (e.key === 'Escape') {
        selectLayer(null);
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [undo, redo, selectLayer, canUndo, canRedo]);
}
